// ── Getting people out of a phone's address book ────────────────────────────────
//
// The blunt fact: **a web page cannot read an iPhone's contacts.** The Contact Picker API
// (`navigator.contacts.select`) is Chrome-on-Android only, and every iOS browser — Safari, Chrome,
// Firefox — is WebKit underneath, which has never shipped it. This is not a permission we can ask
// for; the API does not exist there. Anything claiming otherwise on iOS is a native app.
//
// So there are three routes, and which one a device gets depends on what it actually supports:
//
//   1. Contact Picker  — Android Chrome. The real thing: a native multi-select returning name/tel/email.
//   2. Web Share       — iOS and Android. We can't read contacts, but we can hand the invite link to
//                        the OS share sheet, and the user picks people inside Messages / WhatsApp /
//                        Mail, which do have contact access. On iPhone this is the practical answer.
//   3. vCard import    — everywhere. iOS Contacts → select → Share → Save to Files → upload the .vcf.
//                        Clunky, but it's the only route that gets many contacts in at once on iOS.

export function hasContactPicker() {
  return typeof navigator !== 'undefined'
    && 'contacts' in navigator
    && typeof navigator.contacts?.select === 'function';
}

export function hasWebShare() {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

export function isIOS() {
  if (typeof navigator === 'undefined') return false;
  // iPadOS 13+ reports itself as a Mac, so the touch check is what catches it.
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

// Open the native picker and flatten the result into the phone/email strings the invite form takes.
// Only ever call this from a click handler — the API requires a user gesture.
export async function pickDeviceContacts() {
  if (!hasContactPicker()) throw new Error('This browser can’t open your contacts');
  const selected = await navigator.contacts.select(['name', 'tel', 'email'], { multiple: true });
  const phones = new Set(), emails = new Set(), names = [];
  for (const c of selected || []) {
    (c.tel || []).forEach(t => t && phones.add(String(t).trim()));
    (c.email || []).forEach(e => e && emails.add(String(e).trim()));
    if (c.name?.[0]) names.push(c.name[0]);
  }
  return { phones: [...phones], emails: [...emails], names, count: (selected || []).length };
}

// Hand a link to the OS share sheet. Returns 'shared' | 'cancelled' | 'unsupported'.
export async function shareInvite({ title, text, url }) {
  if (!hasWebShare()) return 'unsupported';
  try {
    await navigator.share({ title, text, url });
    return 'shared';
  } catch (err) {
    // AbortError just means the user closed the sheet — not a failure worth surfacing.
    if (err?.name === 'AbortError') return 'cancelled';
    throw err;
  }
}
